export type Order = "asc" | "desc";
const charRegex = new RegExp(/[\][]/, "gm");

function isNumeric(value: string): boolean {
  return !isNaN(Number(value));
}

export function desc<T extends Record<string, any>>(a: T, b: T, orderBy: keyof T): number {
  const id = orderBy.toString();
  const indexWithChar: number = id.search(charRegex);

  const firstDataName: string = indexWithChar > -1 ? id.substring(0, indexWithChar) : id;
  const secondDataName: string | null = indexWithChar > -1 ? id.substring(indexWithChar + 1, id.length - 1) : null;

  let aItem: any, bItem: any;

  // Case where you have an object 3 objects deep [object1][object2][object3]
  if (firstDataName && secondDataName?.includes("][")) {
    const indexOfThirdKey: number = secondDataName.indexOf("][");
    const secondKey = secondDataName.substring(0, indexOfThirdKey);
    const thirdKey = secondDataName.substring(indexOfThirdKey + 2, secondDataName.length);

    aItem = a[firstDataName as keyof T]?.[secondKey]?.[thirdKey] ?? null;
    bItem = b[firstDataName as keyof T]?.[secondKey]?.[thirdKey] ?? null;
  } else if (firstDataName && secondDataName) {
    aItem = a[firstDataName as keyof T]?.[secondDataName] ?? null;
    bItem = b[firstDataName as keyof T]?.[secondDataName] ?? null;
  } else {
    aItem = a[firstDataName as keyof T] ?? null;
    bItem = b[firstDataName as keyof T] ?? null;
  }

  if (aItem === bItem) {
    return 0;
  } else if (aItem === null || bItem === null) {
    return 0;
  } else if (typeof aItem === "string" && typeof bItem === "string") {
    if (isNumeric(aItem) && isNumeric(bItem)) {
      return parseInt(bItem) < parseInt(aItem) ? -1 : 1;
    }
    return bItem < aItem ? -1 : 1;
  } else if (typeof aItem === "number" && typeof bItem === "number") {
    return bItem < aItem ? -1 : 1;
  } else if (typeof aItem === "object" && typeof bItem === "object") {
    if (firstDataName === "pairing" && aItem?.shortId) {
      return bItem.shortId < aItem.shortId ? -1 : 1;
    }
  }

  // Special case for flightNumber sorting
  if (firstDataName === "_id" && (a as any)?.flightNumber) {
    return (b as any)?.flightNumber < (a as any)?.flightNumber ? -1 : 1;
  }

  return 0;
}

export function stableSort<T>(array: any[], cmp: (a: T, b: T) => number): any[] {
  const stabilizedThis = array.map((el, index) => [el, index] as [any, number]);
  stabilizedThis.sort((a, b) => {
    const order = cmp(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

export function getSorting<K extends keyof any>(
  order: Order,
  orderBy: K
): (a: { [key in K]: number | string }, b: { [key in K]: number | string }) => number {
  return order === "desc" ? (a, b) => desc(a, b, orderBy) : (a, b) => -desc(a, b, orderBy);
}
